/*
 * Enhanced product image zoom
 * - Desktop (fine pointer): hover lens that follows the cursor (inner zoom).
 * - Touch devices: pinch-to-zoom, drag-to-pan and double-tap toggle inside the lightbox.
 *
 * This module is intentionally self-contained and does not depend on magnify.js.
 * When the merchant's "Image zoom" setting is "hover", stock magnify.js handles
 * the desktop interaction, so the hover lens here is skipped to avoid duplication.
 */
(function () {
  'use strict';

  var DESKTOP_ZOOM = 2.2; // magnification for the desktop hover lens
  var MAX_PINCH_SCALE = 4; // maximum pinch zoom on touch
  var DOUBLE_TAP_SCALE = 2.5; // zoom level applied on double-tap
  var DOUBLE_TAP_MS = 300;

  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var coarsePointer = window.matchMedia('(pointer: coarse)').matches;

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  /* ----------------------------------------------------------------
   * Desktop hover lens
   * ---------------------------------------------------------------- */
  function initHoverLens(root) {
    if (!finePointer) return;

    var openers = root.querySelectorAll('.product__modal-opener--image');
    openers.forEach(function (opener) {
      // Skip if the merchant uses the stock "hover" zoom (magnify.js handles it).
      var productInfo = opener.closest('product-info, [data-zoom-on-hover]');
      if (productInfo && productInfo.hasAttribute('data-zoom-on-hover')) return;

      if (opener.dataset.hoverLensBound === 'true') return;
      opener.dataset.hoverLensBound = 'true';

      var mediaEl = opener.querySelector('.product__media');
      var img = opener.querySelector('.product__media img');
      if (!mediaEl || !img) return;

      // Zoom by scaling the actual image (keeps its real aspect ratio) and
      // moving the transform-origin to follow the cursor. This avoids the
      // distortion caused by stretching a background image to the box ratio.
      function showLens() {
        img.classList.add('pk-hover-zoom');
      }

      function moveLens(event) {
        var rect = mediaEl.getBoundingClientRect();
        var xPercent = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
        var yPercent = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
        img.style.transformOrigin = xPercent + '% ' + yPercent + '%';
        img.style.transform = 'scale(' + DESKTOP_ZOOM + ')';
      }

      function hideLens() {
        img.classList.remove('pk-hover-zoom');
        img.style.transform = '';
        img.style.transformOrigin = '';
      }

      // Listen on the opener (the toggle button overlay sits above .product__media,
      // so pointer events bubble to the common ancestor rather than the media element).
      opener.addEventListener('pointerenter', function (event) {
        if (event.pointerType !== 'mouse') return;
        showLens();
        moveLens(event);
      });
      opener.addEventListener('pointermove', function (event) {
        if (event.pointerType !== 'mouse') return;
        moveLens(event);
      });
      opener.addEventListener('pointerleave', hideLens);
    });
  }

  /* ----------------------------------------------------------------
   * Touch lightbox: pinch zoom, pan, double-tap
   * ---------------------------------------------------------------- */
  function initTouchZoom(root) {
    if (!coarsePointer) return;

    root.querySelectorAll('.product-media-modal__content img').forEach(setupPinchZoom);

    // Re-bind images that get injected later (e.g. variant change / product swap).
    root.querySelectorAll('.product-media-modal__content').forEach(observeModalContent);
  }

  function observeModalContent(content) {
    if (content.dataset.pinchObserved === 'true') return;
    content.dataset.pinchObserved = 'true';

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'IMG') {
            setupPinchZoom(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('img').forEach(setupPinchZoom);
          }
        });
      });
    });

    observer.observe(content, { childList: true, subtree: true });
  }

  function setupPinchZoom(img) {
    if (img.dataset.pinchBound === 'true') return;
    img.dataset.pinchBound = 'true';

    var scale = 1;
    var startScale = 1;
    var tx = 0;
    var ty = 0;
    var startDistance = 0;
    var lastTap = 0;
    var pointers = new Map();

    function apply() {
      img.style.transform = 'translate(' + tx + 'px, ' + ty + 'px) scale(' + scale + ')';
      img.classList.toggle('pk-zoomed', scale > 1.01);
    }

    function reset() {
      scale = 1;
      tx = 0;
      ty = 0;
      apply();
    }

    function pointerList() {
      return Array.from(pointers.values());
    }

    img.addEventListener('pointerdown', function (event) {
      img.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (pointers.size === 2) {
        var p = pointerList();
        startDistance = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
        startScale = scale;
      }
    });

    img.addEventListener('pointermove', function (event) {
      if (!pointers.has(event.pointerId)) return;
      var previous = pointers.get(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (pointers.size >= 2) {
        event.preventDefault();
        var p = pointerList();
        var distance = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
        if (startDistance > 0) {
          scale = clamp(startScale * (distance / startDistance), 1, MAX_PINCH_SCALE);
          apply();
        }
      } else if (pointers.size === 1 && scale > 1.01) {
        event.preventDefault();
        tx += event.clientX - previous.x;
        ty += event.clientY - previous.y;
        apply();
      }
    });

    function endPointer(event) {
      pointers.delete(event.pointerId);
      if (pointers.size < 2 && scale <= 1.01) {
        reset();
      }
    }

    img.addEventListener('pointerup', endPointer);
    img.addEventListener('pointercancel', endPointer);

    // Double-tap to toggle zoom.
    img.addEventListener('click', function () {
      var now = Date.now();
      if (now - lastTap < DOUBLE_TAP_MS) {
        if (scale > 1.01) {
          reset();
        } else {
          scale = DOUBLE_TAP_SCALE;
          apply();
        }
      }
      lastTap = now;
    });
  }

  /* ----------------------------------------------------------------
   * Bootstrap
   * ---------------------------------------------------------------- */
  function init(root) {
    var scope = root || document;
    initHoverLens(scope);
    initTouchZoom(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Re-bind after Shopify theme-editor section reloads.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });

  // Re-bind after product-info re-renders (variant change / product swap).
  document.addEventListener('product-info:loaded', function (event) {
    init(event.target || document);
  });
})();
